import { computed, ref, watch } from 'vue';
import { defineStore, type StoreGeneric, storeToRefs } from 'pinia';
import { Cursor, Identity, ContentCollection } from '@ilteducation/content-collections-types';
import useContentCollectionsStore from './content-collections';
import { getOwnedCollections } from './api';
import { MemberRole } from './types';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useMyProfiles } from '@ilteducation/profile';

const useMyMemberCollectionsStore = defineStore('my-member-collections', () => {
  const collectionIds = ref<string[]>([]);
  const isLoading = ref(false);
  const cursor = ref<Cursor>();

  const { collectionsByIds, put: putInContentStore } = useContentCollectionsStore();
  const { user } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  const { current: currentProfile } = useMyProfiles();

  const hasMore = computed(() => cursor.value !== null);
  const collections = computed<ContentCollection[]>(() => {
    return collectionsByIds(collectionIds) as ContentCollection[];
  });

  const identity = computed<Identity>(() => ({
    userId: user.value.uid,
    profileId: currentProfile.value?.id,
  }));

  watch(identity, () => {
    // reset on identity change
    collectionIds.value = [];
    cursor.value = undefined;
  });

  const load = async (context: string, limit: number, role?: MemberRole): Promise<void> => {
    if (!hasMore.value || isLoading.value) {
      return;
    }

    isLoading.value = true;
    try {
      const { data, cursor: newCursor } = await getOwnedCollections(
        context,
        identity.value.profileId,
        cursor.value as string | undefined,
        limit,
        role,
      );

      putInContentStore(data);

      const existingIds = new Set(collectionIds.value);
      data.forEach((collection) => {
        if (!existingIds.has(collection.id)) {
          collectionIds.value.push(collection.id);
        }
      });

      cursor.value = newCursor;
    } catch (error) {
      console.error('Failed to load member collections:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const $reset = () => {
    collectionIds.value = [];
    isLoading.value = false;
    cursor.value = undefined;
  };

  return {
    collections,
    isLoading: computed(() => isLoading.value),
    hasMore,
    load,
    $reset,
  };
});

export default useMyMemberCollectionsStore;
